const escapeHtml = (value) => {
    return String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
};

const isSafeUrl = (url) => {
    const value = String(url ?? '').trim();
    if (!value) {
        return false;
    }

    const normalized = value.toLowerCase();
    return (
        normalized.startsWith('http://') ||
        normalized.startsWith('https://') ||
        normalized.startsWith('mailto:') ||
        normalized.startsWith('/')
    );
};

const renderInlineMarkdown = (value) => {
    let text = escapeHtml(value);
    const codeTokens = [];
    const linkTokens = [];

    text = text.replace(/`([^`]+)`/g, (_, codeValue) => {
        const token = `@@MDCODE${codeTokens.length}@@`;
        codeTokens.push(`<code>${codeValue}</code>`);
        return token;
    });

    text = text.replace(/\[([^\]]+)\]\(([^)]+)\)/g, (_, label, href) => {
        if (!isSafeUrl(href)) {
            return label;
        }
        const token = `@@MDLINK${linkTokens.length}@@`;
        const rel = href.toLowerCase().startsWith('http') ? ' rel="noopener noreferrer"' : '';
        linkTokens.push(`<a href="${escapeHtml(href)}"${rel}>${label}</a>`);
        return token;
    });

    text = text.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
    text = text.replace(/__([^_]+)__/g, '<strong>$1</strong>');
    text = text.replace(/\*([^*]+)\*/g, '<em>$1</em>');
    text = text.replace(/_([^_]+)_/g, '<em>$1</em>');
    text = text.replace(/~~([^~]+)~~/g, '<del>$1</del>');

    linkTokens.forEach((htmlValue, index) => {
        text = text.split(`@@MDLINK${index}@@`).join(htmlValue);
    });
    codeTokens.forEach((htmlValue, index) => {
        text = text.split(`@@MDCODE${index}@@`).join(htmlValue);
    });

    return text;
};

const isBlankLine = (line) => !String(line ?? '').trim();
const isHeadingLine = (line) => /^#{1,6}\s+/.test(line);
const isUnorderedListLine = (line) => /^\s*[-*+]\s+/.test(line);
const isOrderedListLine = (line) => /^\s*\d+\.\s+/.test(line);
const isBlockQuoteLine = (line) => /^\s*>\s?/.test(line);
const isFenceLine = (line) => /^\s*```/.test(line);
const isHorizontalRuleLine = (line) => /^\s{0,3}([-*_])(?:\s*\1){2,}\s*$/.test(line);

const isBlockStartLine = (line) => (
    isFenceLine(line) ||
    isHeadingLine(line) ||
    isUnorderedListLine(line) ||
    isOrderedListLine(line) ||
    isBlockQuoteLine(line) ||
    isHorizontalRuleLine(line)
);

const renderMarkdownToSafeHtml = (markdown) => {
    const lines = String(markdown ?? '')
        .replace(/\r\n/g, '\n')
        .replace(/\r/g, '\n')
        .split('\n');
    const html = [];
    let index = 0;
    let codeFenceLanguage = '';
    let codeFenceLines = [];
    let inCodeFence = false;
    let inUnorderedList = false;
    let inOrderedList = false;

    const closeLists = () => {
        if (inUnorderedList) {
            html.push('</ul>');
            inUnorderedList = false;
        }
        if (inOrderedList) {
            html.push('</ol>');
            inOrderedList = false;
        }
    };

    const flushCodeFence = () => {
        const languageClass = codeFenceLanguage ? ` class="language-${escapeHtml(codeFenceLanguage)}"` : '';
        html.push(`<pre><code${languageClass}>${escapeHtml(codeFenceLines.join('\n'))}</code></pre>`);
        inCodeFence = false;
        codeFenceLanguage = '';
        codeFenceLines = [];
    };

    while (index < lines.length) {
        const line = lines[index];

        if (inCodeFence) {
            if (isFenceLine(line)) {
                flushCodeFence();
            } else {
                codeFenceLines.push(line);
            }
            index += 1;
            continue;
        }

        const fenceMatch = line.match(/^\s*```([\w-]*)\s*$/);
        if (fenceMatch) {
            closeLists();
            inCodeFence = true;
            codeFenceLanguage = String(fenceMatch[1] ?? '').trim();
            codeFenceLines = [];
            index += 1;
            continue;
        }

        if (isBlankLine(line)) {
            closeLists();
            index += 1;
            continue;
        }

        if (isHorizontalRuleLine(line)) {
            closeLists();
            html.push('<hr />');
            index += 1;
            continue;
        }

        const headingMatch = line.match(/^(#{1,6})\s+(.*)$/);
        if (headingMatch) {
            closeLists();
            const level = headingMatch[1].length;
            html.push(`<h${level}>${renderInlineMarkdown(headingMatch[2])}</h${level}>`);
            index += 1;
            continue;
        }

        const unorderedListMatch = line.match(/^\s*[-*+]\s+(.*)$/);
        if (unorderedListMatch) {
            if (inOrderedList) {
                html.push('</ol>');
                inOrderedList = false;
            }
            if (!inUnorderedList) {
                html.push('<ul>');
                inUnorderedList = true;
            }
            html.push(`<li>${renderInlineMarkdown(unorderedListMatch[1])}</li>`);
            index += 1;
            continue;
        }

        const orderedListMatch = line.match(/^\s*\d+\.\s+(.*)$/);
        if (orderedListMatch) {
            if (inUnorderedList) {
                html.push('</ul>');
                inUnorderedList = false;
            }
            if (!inOrderedList) {
                html.push('<ol>');
                inOrderedList = true;
            }
            html.push(`<li>${renderInlineMarkdown(orderedListMatch[1])}</li>`);
            index += 1;
            continue;
        }

        if (isBlockQuoteLine(line)) {
            closeLists();
            const quoteLines = [];
            while (index < lines.length && isBlockQuoteLine(lines[index])) {
                quoteLines.push(lines[index].replace(/^\s*>\s?/, ''));
                index += 1;
            }
            html.push(`<blockquote><p>${renderInlineMarkdown(quoteLines.join(' '))}</p></blockquote>`);
            continue;
        }

        closeLists();
        const paragraphLines = [line.trim()];
        index += 1;
        while (
            index < lines.length &&
            !isBlankLine(lines[index]) &&
            !isBlockStartLine(lines[index])
        ) {
            paragraphLines.push(lines[index].trim());
            index += 1;
        }
        html.push(`<p>${renderInlineMarkdown(paragraphLines.join(' '))}</p>`);
    }

    closeLists();
    if (inCodeFence) {
        flushCodeFence();
    }

    return html.join('\n');
};

export default renderMarkdownToSafeHtml;
